import React, { useEffect, useState } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import MicButton from './MicButton';

// Dictated text joins what is already typed, without the closing full stop.
const withDictation = (previous, text) => [previous, text.trim().replace(/[.。]+$/, '')].filter(Boolean).join(' ');
// A number field keeps the number of what was dictated: "1 500 kg." -> "1500", "2,5" -> "2.5".
const dictatedNumber = (text) => {
  const match = text.replace(/(\d)[\s  ](?=\d{3}\b)/g, '$1').match(/-?\d+(?:[.,]\d+)?/);
  return match ? match[0].replace(',', '.') : '';
};

/** One guided-assessment question: options, an "other" answer, or a value typed or dictated. */
export default function CreditQuestionCard({ questionnaire, disabled, onSubmit, t }) {
  const [selectedValues, setSelectedValues] = useState([]);
  const [freeText, setFreeText] = useState('');
  const [otherEnabled, setOtherEnabled] = useState(false);
  const [otherText, setOtherText] = useState('');

  useEffect(() => {
    setSelectedValues([]);
    setFreeText('');
    setOtherEnabled(false);
    setOtherText('');
  }, [questionnaire?.id]);

  if (!questionnaire) return null;

  const inputType = questionnaire.input_type || 'text';
  const options = questionnaire.options || [];
  const selectedOptions = options.filter((option) => selectedValues.includes(option.value));

  const toggleOption = (value) => {
    if (disabled) return;

    if (inputType === 'single_select') {
      setSelectedValues((prev) => (prev[0] === value ? [] : [value]));
      return;
    }

    setSelectedValues((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const buildSubmission = () => {
    const normalizedFreeText = freeText.trim();
    const normalizedOtherText = otherText.trim();

    let answerText = '';
    if (inputType === 'multi_select') {
      const labels = selectedOptions.map((option) => option.label);
      if (normalizedOtherText) labels.push(normalizedOtherText);
      answerText = labels.join(', ');
    } else if (inputType === 'single_select') {
      // A precision typed next to a chosen option is part of the answer, not a
      // replacement for it — show both, and send both (see _normalize_answer_payload).
      const chosen = selectedOptions[0]?.label;
      answerText = chosen
        ? [chosen, normalizedOtherText].filter(Boolean).join(' - ')
        : normalizedOtherText || normalizedFreeText;
    } else {
      answerText = [normalizedFreeText, normalizedOtherText].filter(Boolean).join(' - ');
    }

    return {
      answerText,
      payload: {
        question_id: questionnaire.id,
        field_key: questionnaire.field_key,
        question: questionnaire.question,
        input_type: inputType,
        selected_options: selectedOptions,
        other_text: normalizedOtherText || null,
        free_text: normalizedFreeText || null,
      },
    };
  };

  const { answerText, payload } = buildSubmission();
  const canSubmit = Boolean(answerText);

  return (
    <div className="credit-question-card">
      <div className="credit-question-title">{questionnaire.question}</div>
      {questionnaire.helper_text && (
        <div className="credit-question-helper">{questionnaire.helper_text}</div>
      )}

      {options.length > 0 && (
        <div className="credit-question-options">
          {options.map((option) => {
            const selected = selectedValues.includes(option.value);
            return (
              <button
                key={`${questionnaire.id}-${option.value}`}
                type="button"
                className={`credit-option-btn${selected ? ' selected' : ''}`}
                aria-pressed={selected}
                disabled={disabled}
                onClick={() => toggleOption(option.value)}
              >
                <span>{option.label}</span>
                {option.description && (
                  <small>{option.description}</small>
                )}
              </button>
            );
          })}
        </div>
      )}

      {questionnaire.allow_other && (
        <div className="credit-other-wrapper">
          <button
            type="button"
            className={`credit-other-toggle${otherEnabled ? ' selected' : ''}`}
            disabled={disabled}
            onClick={() => setOtherEnabled((prev) => !prev)}
          >
            {questionnaire.other_label || t('chat.credit_other', 'Autre')}
          </button>
          {otherEnabled && (
            <div className="credit-input-row">
              <input
                type="text"
                className="credit-other-input"
                value={otherText}
                disabled={disabled}
                placeholder={questionnaire.placeholder || t('chat.credit_other_placeholder', 'Précisez votre réponse')}
                onChange={(e) => setOtherText(e.target.value)}
              />
              <MicButton disabled={disabled} title={t('workspace:dictate')} answerTo={questionnaire.question}
                onTranscribe={(text) => setOtherText((previous) => withDictation(previous, text))} />
            </div>
          )}
        </div>
      )}

      {(inputType === 'text' || inputType === 'number') && (
        <div className="credit-input-row">
          <input
            type={inputType === 'number' ? 'number' : 'text'}
            className="credit-free-input"
            value={freeText}
            disabled={disabled}
            placeholder={questionnaire.placeholder || t('chat.credit_text_placeholder', 'Entrez votre réponse')}
            onChange={(e) => setFreeText(e.target.value)}
          />
          <MicButton disabled={disabled} title={t('workspace:dictate')} answerTo={questionnaire.question}
            onTranscribe={(text) => setFreeText((previous) => (inputType === 'number'
              ? dictatedNumber(text) || previous : withDictation(previous, text)))} />
        </div>
      )}

      {questionnaire.unit && (
        <div className="credit-question-unit">
          {t('chat.credit_unit', 'Unité')} : {questionnaire.unit}
        </div>
      )}

      <div className="credit-question-actions">
        <button
          type="button"
          className="credit-submit-btn"
          disabled={disabled || !canSubmit}
          onClick={() => onSubmit(answerText, payload)}
          aria-label={t('chat.credit_continue', 'Continuer')}
        >
          <span>{t('chat.credit_continue', 'Continuer')}</span><FaArrowRight />
        </button>
      </div>
    </div>
  );
}
